import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Mail, MapPin, Facebook, ShieldCheck } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { SectionTag } from "@/components/Reveal";
import { useSiteContent } from "@/context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SKYLINE_BG =
    "https://images.unsplash.com/photo-1709755813430-5dbd547214df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjaW5jaW5uYXRpJTIwc2t5bGluZSUyMG5pZ2h0fGVufDB8fHx8MTc4Njg4NDQ3MXww&ixlib=rb-4.1.0&q=85";

const inputCls =
    "w-full rounded-none border border-zinc-800 bg-surface px-4 py-3.5 text-sm text-white placeholder:text-zinc-600 outline-none transition-[border-color,box-shadow] duration-300 focus:border-neon focus:ring-1 focus:ring-neon";

const steps = ["Who You Are", "The Project", "Details"];

export default function Contact() {
    const { CONTACT_EMAIL, LOCATION, SOCIALS, serviceOptions, budgetOptions, timelineOptions, VENMO, DEPOSIT_LINK } = useSiteContent();
    const [step, setStep] = useState(1);
    const [form, setForm] = useState({
        name: "", email: "", business_name: "", service: "", budget: "", timeline: "", message: "", company: "", tos: false,
    });
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle");
    const [quoteId, setQuoteId] = useState(null);

    const set = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = (s) => {
        const e = {};
        if (s === 1) {
            if (!form.name.trim()) e.name = "Your name is required";
            if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "A valid email is required";
        }
        if (s === 2 && !form.service) e.service = "Pick a service";
        if (s === 3 && !form.tos) e.tos = "Please agree to the Terms of Service to send your request";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const next = () => validate(step) && setStep(step + 1);
    const back = () => setStep(step - 1);

    const submit = async () => {
        if (!validate(3)) return;
        if (form.company) {
            setStatus("success");
            return;
        }
        setStatus("sending");
        try {
            const { data } = await axios.post(`${API}/quotes`, {
                name: form.name,
                email: form.email,
                business_name: form.business_name,
                service: form.service,
                budget: form.budget,
                timeline: form.timeline,
                message: form.message,
                tos_accepted: form.tos,
            });
            setQuoteId(data.id);
            setStatus("success");
        } catch (err) {
            setStatus("idle");
            toast.error(`Something went wrong sending your request. Please email ${CONTACT_EMAIL} directly.`);
        }
    };

    return (
        <div data-testid="contact-page">
            <SEO
                title="Request a Quote | Contact Camarca Digital Designs — Cincinnati, Ohio"
                description="Start your website, logo or branding project with Camarca Digital Designs. Request a free quote — serving Cincinnati, Ohio and small businesses nationwide."
                path="/contact"
            />

            <div className="grid min-h-screen lg:grid-cols-2">
                {/* LEFT — INFO */}
                <div className="relative overflow-hidden border-b border-white/10 lg:border-b-0 lg:border-r">
                    <img src={SKYLINE_BG} alt="Cincinnati skyline at night" className="absolute inset-0 h-full w-full object-cover opacity-30" />
                    <div className="absolute inset-0 bg-gradient-to-t from-void via-void/70 to-void/40" aria-hidden="true" />
                    <div className="relative flex h-full flex-col justify-end p-8 pt-40 lg:p-16">
                        <SectionTag>Contact</SectionTag>
                        <h1 className="mt-6 font-display text-4xl font-black leading-tight tracking-tight md:text-5xl">
                            Tell me about{" "}
                            <span className="bg-gradient-to-r from-neon to-deep bg-clip-text text-transparent">your project.</span>
                        </h1>
                        <p className="mt-6 max-w-md leading-relaxed text-zinc-400">
                            Fill out the form and you'll get a personal reply by email with next steps and pricing confirmation.
                        </p>
                        <div className="mt-10 space-y-5">
                            <a
                                href={`mailto:${CONTACT_EMAIL}`}
                                data-testid="contact-email-link"
                                className="group flex items-center gap-4 text-sm text-zinc-300 transition-colors duration-300 hover:text-neon"
                            >
                                <span className="flex h-10 w-10 items-center justify-center border border-white/15 text-neon">
                                    <Mail className="h-4 w-4" />
                                </span>
                                {CONTACT_EMAIL}
                            </a>
                            <p className="flex items-center gap-4 text-sm text-zinc-400">
                                <span className="flex h-10 w-10 items-center justify-center border border-white/15 text-neon">
                                    <MapPin className="h-4 w-4" />
                                </span>
                                {LOCATION}
                            </p>
                            <div className="flex gap-3 pt-2">
                                <a
                                    href={SOCIALS.facebook}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-facebook-link"
                                    aria-label="Facebook"
                                    className="flex h-10 w-10 items-center justify-center border border-white/15 text-zinc-400 transition-[color,border-color] duration-300 hover:border-neon hover:text-neon"
                                >
                                    <Facebook className="h-4 w-4" />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT — FORM */}
                <div className="flex items-center px-6 py-16 pt-32 lg:px-16 lg:pt-40">
                    <div className="w-full max-w-xl">
                        {status === "success" ? (
                            <motion.div
                                data-testid="quote-success-panel"
                                initial={{ opacity: 0, y: 24 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7 }}
                            >
                                <CheckCircle2 className="h-14 w-14 text-neon" />
                                <h2 className="mt-8 font-display text-3xl font-bold">Request received.</h2>
                                <p className="mt-4 leading-relaxed text-zinc-400">
                                    Thanks{form.name ? `, ${form.name}` : ""} — your {form.service || "project"} request is in.
                                    A confirmation email is on its way to <span className="text-white">{form.email}</span>, and
                                    I'll reply personally with next steps.
                                </p>
                                <p className="mt-4 border-l-2 border-neon/50 pl-4 text-sm leading-relaxed text-zinc-400">
                                    Next step: I'll reply with scope and total. A 40% deposit starts your build —{" "}
                                    {VENMO ? <>pay by card below or Venmo <span className="text-neon">@{VENMO}</span>.</> : "pay by card below."}
                                </p>
                                {quoteId && (
                                    <p className="mt-6 font-mono text-xs tracking-widest text-zinc-600">REF: {quoteId}</p>
                                )}
                                <div className="mt-10 flex flex-wrap items-center gap-4">
                                    {DEPOSIT_LINK && (
                                        <a
                                            href={DEPOSIT_LINK}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            data-testid="success-pay-deposit-button"
                                            className="group inline-flex items-center gap-3 bg-neon px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)]"
                                        >
                                            <ShieldCheck className="h-4 w-4" />
                                            Pay Your Deposit
                                        </a>
                                    )}
                                    <Link
                                        to="/work"
                                        data-testid="success-view-work-link"
                                        className="group inline-flex items-center gap-3 border border-neon/70 px-6 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color] duration-300 hover:bg-neon hover:text-void"
                                    >
                                        Browse my work
                                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                    </Link>
                                </div>
                            </motion.div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between">
                                    <p className="font-mono text-xs tracking-[0.25em] uppercase text-zinc-500">
                                        Step <span className="text-neon">0{step}</span> / 03 — {steps[step - 1]}
                                    </p>
                                    <div className="flex gap-1.5">
                                        {[1, 2, 3].map((s) => (
                                            <span
                                                key={s}
                                                className={`h-1 w-8 transition-colors duration-500 ${s <= step ? "bg-neon" : "bg-zinc-800"}`}
                                            />
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-10">
                                    <AnimatePresence mode="wait">
                                        {step === 1 && (
                                            <motion.div
                                                key="step1"
                                                initial={{ opacity: 0, x: 40 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -40 }}
                                                transition={{ duration: 0.4 }}
                                                className="space-y-6"
                                            >
                                                <div>
                                                    <label htmlFor="quote-name" className="mb-2 block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Your Name *</label>
                                                    <input
                                                        id="quote-name"
                                                        data-testid="quote-name-input"
                                                        className={inputCls}
                                                        value={form.name}
                                                        onChange={(e) => set("name", e.target.value)}
                                                        placeholder="Jane Smith"
                                                    />
                                                    {errors.name && <p data-testid="quote-name-error" className="mt-2 font-mono text-xs text-red-400">{errors.name}</p>}
                                                </div>
                                                <div>
                                                    <label htmlFor="quote-email" className="mb-2 block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Email *</label>
                                                    <input
                                                        id="quote-email"
                                                        type="email"
                                                        data-testid="quote-email-input"
                                                        className={inputCls}
                                                        value={form.email}
                                                        onChange={(e) => set("email", e.target.value)}
                                                        placeholder="you@business.com"
                                                    />
                                                    {errors.email && <p data-testid="quote-email-error" className="mt-2 font-mono text-xs text-red-400">{errors.email}</p>}
                                                </div>
                                                <div>
                                                    <label htmlFor="quote-business" className="mb-2 block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Business Name</label>
                                                    <input
                                                        id="quote-business"
                                                        data-testid="quote-business-input"
                                                        className={inputCls}
                                                        value={form.business_name}
                                                        onChange={(e) => set("business_name", e.target.value)}
                                                        placeholder="Smith's Auto Repair"
                                                    />
                                                </div>
                                                <input
                                                    type="text"
                                                    name="company"
                                                    tabIndex={-1}
                                                    autoComplete="off"
                                                    aria-hidden="true"
                                                    className="hidden"
                                                    value={form.company}
                                                    onChange={(e) => set("company", e.target.value)}
                                                />
                                            </motion.div>
                                        )}

                                        {step === 2 && (
                                            <motion.div
                                                key="step2"
                                                initial={{ opacity: 0, x: 40 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -40 }}
                                                transition={{ duration: 0.4 }}
                                                className="space-y-6"
                                            >
                                                <div>
                                                    <label className="mb-2 block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">What do you need? *</label>
                                                    <Select value={form.service} onValueChange={(v) => set("service", v)}>
                                                        <SelectTrigger data-testid="quote-service-select" className="rounded-none border-zinc-800 bg-surface px-4 py-3.5 text-sm focus:border-neon focus:ring-1 focus:ring-neon">
                                                            <SelectValue placeholder="Choose a service" />
                                                        </SelectTrigger>
                                                        <SelectContent className="rounded-none border-zinc-800 bg-raised">
                                                            {serviceOptions.map((o) => (
                                                                <SelectItem key={o} value={o} data-testid={`service-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                                                                    {o}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    {errors.service && <p data-testid="quote-service-error" className="mt-2 font-mono text-xs text-red-400">{errors.service}</p>}
                                                </div>
                                                <div>
                                                    <label className="mb-2 block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Budget</label>
                                                    <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                                                        <SelectTrigger data-testid="quote-budget-select" className="rounded-none border-zinc-800 bg-surface px-4 py-3.5 text-sm focus:border-neon focus:ring-1 focus:ring-neon">
                                                            <SelectValue placeholder="Choose a range" />
                                                        </SelectTrigger>
                                                        <SelectContent className="rounded-none border-zinc-800 bg-raised">
                                                            {budgetOptions.map((o) => (
                                                                <SelectItem key={o} value={o}>{o}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <label className="mb-2 block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Timeline</label>
                                                    <Select value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                                                        <SelectTrigger data-testid="quote-timeline-select" className="rounded-none border-zinc-800 bg-surface px-4 py-3.5 text-sm focus:border-neon focus:ring-1 focus:ring-neon">
                                                            <SelectValue placeholder="When do you need it?" />
                                                        </SelectTrigger>
                                                        <SelectContent className="rounded-none border-zinc-800 bg-raised">
                                                            {timelineOptions.map((o) => (
                                                                <SelectItem key={o} value={o}>{o}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </motion.div>
                                        )}

                                        {step === 3 && (
                                            <motion.div
                                                key="step3"
                                                initial={{ opacity: 0, x: 40 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                exit={{ opacity: 0, x: -40 }}
                                                transition={{ duration: 0.4 }}
                                                className="space-y-6"
                                            >
                                                <div>
                                                    <label htmlFor="quote-message" className="mb-2 block font-mono text-xs tracking-[0.2em] uppercase text-zinc-500">Project Details</label>
                                                    <textarea
                                                        id="quote-message"
                                                        data-testid="quote-message-input"
                                                        rows={5}
                                                        className={`${inputCls} resize-none`}
                                                        value={form.message}
                                                        onChange={(e) => set("message", e.target.value)}
                                                        placeholder="Tell me about your business, what you need, and any sites you like…"
                                                    />
                                                </div>
                                                <div data-testid="quote-review" className="border border-white/10 bg-surface p-5 font-mono text-xs leading-loose text-zinc-400">
                                                    <p><span className="text-zinc-600">NAME —</span> {form.name}</p>
                                                    <p><span className="text-zinc-600">EMAIL —</span> {form.email}</p>
                                                    {form.business_name && <p><span className="text-zinc-600">BUSINESS —</span> {form.business_name}</p>}
                                                    <p><span className="text-zinc-600">SERVICE —</span> <span className="text-neon">{form.service}</span></p>
                                                    {form.budget && <p><span className="text-zinc-600">BUDGET —</span> {form.budget}</p>}
                                                    {form.timeline && <p><span className="text-zinc-600">TIMELINE —</span> {form.timeline}</p>}
                                                </div>
                                                <div>
                                                    <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-zinc-400">
                                                        <input
                                                            type="checkbox"
                                                            data-testid="quote-tos-checkbox"
                                                            checked={form.tos}
                                                            onChange={(e) => set("tos", e.target.checked)}
                                                            className="mt-0.5 h-4 w-4 shrink-0 accent-[#00F0FF]"
                                                        />
                                                        <span>
                                                            I agree to the{" "}
                                                            <Link to="/terms" target="_blank" data-testid="quote-tos-link" className="text-neon underline underline-offset-4 transition-colors duration-300 hover:text-white">
                                                                Terms of Service
                                                            </Link>
                                                            , including the 40% deposit and hosting terms.
                                                        </span>
                                                    </label>
                                                    {errors.tos && <p data-testid="quote-tos-error" className="mt-2 font-mono text-xs text-red-400">{errors.tos}</p>}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>

                                <div className="mt-10 flex items-center justify-between">
                                    {step > 1 ? (
                                        <button
                                            onClick={back}
                                            data-testid="quote-back-button"
                                            className="group flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-zinc-500 transition-colors duration-300 hover:text-white"
                                        >
                                            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
                                            Back
                                        </button>
                                    ) : (
                                        <span />
                                    )}
                                    {step < 3 ? (
                                        <button
                                            onClick={next}
                                            data-testid="quote-next-button"
                                            className="group flex items-center gap-3 border border-neon/70 px-7 py-3.5 font-mono text-xs tracking-[0.2em] uppercase text-neon transition-[background-color,color,box-shadow] duration-300 hover:bg-neon hover:text-void hover:shadow-[0_0_20px_rgba(0,240,255,0.4)]"
                                        >
                                            Continue
                                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={submit}
                                            disabled={status === "sending"}
                                            data-testid="quote-submit-button"
                                            className="group flex items-center gap-3 bg-neon px-7 py-3.5 font-mono text-xs font-medium tracking-[0.2em] uppercase text-void transition-shadow duration-300 hover:shadow-[0_0_25px_rgba(0,240,255,0.5)] disabled:opacity-60"
                                        >
                                            {status === "sending" ? (
                                                <>
                                                    Sending
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                </>
                                            ) : (
                                                <>
                                                    Send Request
                                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
